// Admin-Editor für den Foodosia-Speiseplan (/admin, absichtlich nicht in der Navigation
// verlinkt - nur über die direkte URL erreichbar). Hier werden Standorte und Gerichte
// im Backend-Katalog gepflegt (siehe menuApi.js) und die Zuordnung "welches Gericht an
// welchem Tag" für den rollenden 14-Tage-Plan festgelegt (siehe dailyMenuApi.js).
//
// Aufbau der Datei von oben nach unten:
//   - AdminLogin: Login-Formular (echt gegen Firebase Auth, siehe auth.js, oder ein
//     lokaler Test-Login ohne Firebase-Konfiguration - siehe Kommentar bei handleLogin).
//   - LocationForm / DishForm: Formulare zum Anlegen und Bearbeiten von Standorten
//     bzw. Gerichten. Beide lassen sich sowohl aus-/einblenden bzw. aktivieren/
//     deaktivieren (für z.B. saisonale Pausen) als auch endgültig löschen (siehe
//     ConfirmDialog unten - für versehentliche oder doppelte Testeinträge). Ein
//     deaktivierter Standort taucht in DishForm nicht mehr als Auswahlmöglichkeit
//     für neue Gerichte auf (bestehende Zuordnungen bleiben aber erhalten).
//   - ConfirmDialog: kleiner, wiederverwendbarer Bestätigungs-Dialog ("Wirklich
//     löschen?"), bevor ein Gericht oder Standort endgültig entfernt wird.
//   - ProductRow: ein Gericht in der Produktliste inklusive Bild-Upload-Button.
//   - DayPlanRow: ein Tag im 14-Tage-Plan mit den drei Auswahlfeldern
//     (Tagesgericht, Sondergericht 1, Sondergericht 2).
//   - AdminSpeiseplan: die eigentliche Seite, hält den gesamten Zustand (Login,
//     Standorte, Produkte, Tagesplan) und verbindet die Formulare/Listen oben mit
//     den Backend-Aufrufen aus menuApi.js/dailyMenuApi.js.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { onIdTokenChanged } from "firebase/auth";
import {
  Ban,
  CalendarDays,
  CheckCircle2,
  Image as ImageIcon,
  LogIn,
  LogOut,
  Lock,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  User,
} from "lucide-react";
import styles from "./AdminSpeiseplan.module.css";
import { LOCATION_ID, buildDateWindow, formatPrice } from "../../data/mealPlan";
import {
  createLocation,
  createProduct,
  deleteLocation,
  deleteProduct,
  loadAdminLocations,
  loadAdminProducts,
  setLocationActive,
  setProductAvailability,
  updateLocation,
  updateProduct,
  uploadProductImage,
} from "../../services/menuApi";
import {
  loadDailyPlan,
  resolveCycleSlots,
  saveDailyAssignment,
} from "../../services/dailyMenuApi";
import { firebaseAuth, isFirebaseConfigured } from "../../services/firebase";
import { loginWithEmail, logout as logoutWithFirebase } from "../../services/auth";

const weekdayShortFormatter = new Intl.DateTimeFormat("de-DE", { weekday: "short" });
const dateFormatter = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" });
// Für die Anzeige des Gültigkeitszeitraums (mit Jahr) über dem 14-Tage-Plan.
const rangeFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// Login für den Editor. Ist Firebase konfiguriert (QA/Produktion), wird echt gegen
// Firebase Auth geprüft. Ohne Firebase-Konfiguration (nur bei lokaler Entwicklung ohne
// die REACT_APP_FIREBASE_*-Variablen, siehe firebase.js) genügt ein beliebiger Wert,
// weil das lokale Backend dafür einen festen Test-Token akzeptiert.
function AdminLogin({ onLogin, loginError }) {
  return (
    <main className={styles.loginPage}>
      <section className={styles.loginCard}>
        <div className={styles.loginHero}>
          <div className={styles.lockCircle}>
            <Lock size={32} />
          </div>
          <h1>Admin-Bereich</h1>
          <p>Melde dich an, um den Speiseplan zu bearbeiten</p>
        </div>

        {!isFirebaseConfigured && (
          // Nur lokal sichtbar (kein Firebase konfiguriert): sonst sieht es aus wie ein
          // kaputtes Login, weil hier tatsächlich jede Eingabe durchgelassen wird.
          <p className={styles.localDevNotice}>
            Lokale Entwicklung ohne Firebase: Jede Eingabe loggt dich ein, es findet
            keine echte Prüfung statt. Auf QA/Produktion wird echt gegen Firebase geprüft.
          </p>
        )}

        <form className={styles.loginForm} onSubmit={onLogin}>
          <label>
            E-Mail
            <span className={styles.inputWrap}>
              <User size={18} />
              <input name="email" type="email" required aria-label="E-Mail" />
            </span>
          </label>
          <label>
            Passwort
            <span className={styles.inputWrap}>
              <Lock size={18} />
              <input name="password" type="password" required aria-label="Passwort" />
            </span>
          </label>
          {loginError && <p className={styles.loginErrorText}>{loginError}</p>}
          <button type="submit">
            <LogIn size={20} />
            Anmelden
          </button>
        </form>
      </section>
      <a className={styles.backLink} href="/">
        Zurück zur Startseite
      </a>
    </main>
  );
}

// Formular für neue und bestehende Standorte. Samstag/Sonntag sind bewusst per
// Standard nicht angehakt (die meisten Betriebskantinen haben am Wochenende zu) -
// wer doch am Wochenende bedient, kann das hier gezielt anschalten.
function LocationForm({ editingLocation, onCancelEdit, onSubmitLocation }) {
  const [name, setName] = useState(editingLocation?.name || "");
  const [address, setAddress] = useState(editingLocation?.address || "");
  const [active, setActive] = useState(editingLocation?.active !== false);
  const [serveSaturday, setServeSaturday] = useState(Boolean(editingLocation?.serveSaturday));
  const [serveSunday, setServeSunday] = useState(Boolean(editingLocation?.serveSunday));

  useEffect(() => {
    setName(editingLocation?.name || "");
    setAddress(editingLocation?.address || "");
    setActive(editingLocation?.active !== false);
    setServeSaturday(Boolean(editingLocation?.serveSaturday));
    setServeSunday(Boolean(editingLocation?.serveSunday));
  }, [editingLocation]);

  function handleSubmit(event) {
    event.preventDefault();

    if (!name.trim()) {
      return;
    }

    onSubmitLocation({
      ...editingLocation,
      name: name.trim(),
      address: address.trim(),
      active,
      serveSaturday,
      serveSunday,
    });

    setName("");
    setAddress("");
    setActive(true);
    setServeSaturday(false);
    setServeSunday(false);
  }

  return (
    <form className={styles.locationForm} onSubmit={handleSubmit}>
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Standortname"
        aria-label="Standortname"
      />
      <input
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        placeholder="Adresse oder Beschreibung"
        aria-label="Adresse oder Beschreibung"
      />
      <div className={styles.weekendGroup}>
        <span>Wochenende</span>
        <label className={styles.inlineCheckbox}>
          <input
            type="checkbox"
            checked={serveSaturday}
            onChange={(event) => setServeSaturday(event.target.checked)}
          />
          Samstag geöffnet
        </label>
        <label className={styles.inlineCheckbox}>
          <input
            type="checkbox"
            checked={serveSunday}
            onChange={(event) => setServeSunday(event.target.checked)}
          />
          Sonntag geöffnet
        </label>
      </div>
      {editingLocation && (
        <label className={styles.inlineCheckbox}>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          Aktiv
        </label>
      )}
      <button type="submit">
        <Plus size={18} />
        {editingLocation ? "Standort speichern" : "Standort anlegen"}
      </button>
      {editingLocation && (
        <button className={styles.cancelButton} type="button" onClick={onCancelEdit}>
          Bearbeiten abbrechen
        </button>
      )}
    </form>
  );
}

// Formular für neue und bestehende Gerichte. Ein Gericht kann mehreren Standorten zugewiesen werden.
function DishForm({ editingDish, locations, defaultLocationId, onCancelEdit, onSubmitDish }) {
  const [name, setName] = useState(editingDish?.name || "");
  const [description, setDescription] = useState(editingDish?.description || "");
  const [price, setPrice] = useState(
    editingDish ? String((editingDish.priceCents || 0) / 100).replace(".", ",") : ""
  );
  const [allergens, setAllergens] = useState((editingDish?.allergens || []).join(", "));
  const [available, setAvailable] = useState(editingDish?.available !== false);
  const [locationIds, setLocationIds] = useState(
    editingDish?.locationIds?.length ? editingDish.locationIds : [defaultLocationId]
  );

  useEffect(() => {
    setName(editingDish?.name || "");
    setDescription(editingDish?.description || "");
    setPrice(
      editingDish ? String((editingDish.priceCents || 0) / 100).replace(".", ",") : ""
    );
    setAllergens((editingDish?.allergens || []).join(", "));
    setAvailable(editingDish?.available !== false);
    setLocationIds(
      editingDish?.locationIds?.length ? editingDish.locationIds : [defaultLocationId]
    );
  }, [editingDish, defaultLocationId]);

  function toggleLocation(locationId) {
    setLocationIds((current) =>
      current.includes(locationId)
        ? current.filter((id) => id !== locationId)
        : [...current, locationId]
    );
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (!name.trim() || locationIds.length === 0) {
      return;
    }

    onSubmitDish(
      {
        ...editingDish,
        name: name.trim(),
        description: description.trim() || "Frisch zubereitet von Foodosia",
        priceCents: Math.round(Number(price.replace(",", ".")) * 100) || 0,
        currency: "EUR",
        // Allergene werden mit Komma getrennt eingegeben.
        allergens: allergens
          .split(",")
          .map((allergen) => allergen.trim())
          .filter(Boolean),
        ingredients: editingDish?.ingredients || [],
        available,
      },
      locationIds
    );

    setName("");
    setDescription("");
    setPrice("");
    setAllergens("");
  }

  return (
    <form className={styles.dishForm} onSubmit={handleSubmit}>
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Neues Gericht"
        aria-label="Gerichtname"
      />
      <input
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Beschreibung"
        aria-label="Beschreibung"
      />
      <div className={styles.formRow}>
        <input
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          placeholder="Preis"
          aria-label="Preis"
        />
        <input
          value={allergens}
          onChange={(event) => setAllergens(event.target.value)}
          placeholder="Allergene"
          aria-label="Allergene"
        />
      </div>

      <div className={styles.locationCheckGroup}>
        <span>Standorte</span>
        {/* Deaktivierte Standorte stehen hier bewusst nicht mehr zur Auswahl - ein
            Gericht, das schon einem inzwischen deaktivierten Standort zugeordnet
            war, bleibt aber weiterhin zugeordnet, nur eben nicht mehr sichtbar/wählbar. */}
        {locations.filter((location) => location.active !== false).map((location) => (
          <label key={location.id} className={styles.inlineCheckbox}>
            <input
              type="checkbox"
              checked={locationIds.includes(location.id)}
              onChange={() => toggleLocation(location.id)}
            />
            {location.name}
          </label>
        ))}
      </div>

      <label className={styles.inlineCheckbox}>
        <input
          type="checkbox"
          checked={available}
          onChange={(event) => setAvailable(event.target.checked)}
        />
        Im Speiseplan sichtbar
      </label>

      <button type="submit">
        <Plus size={18} />
        {editingDish ? "Gericht speichern" : "Gericht anlegen"}
      </button>
      {editingDish && (
        <button className={styles.cancelButton} type="button" onClick={onCancelEdit}>
          Bearbeiten abbrechen
        </button>
      )}
    </form>
  );
}

// Kleiner, wiederverwendbarer Bestätigungs-Dialog für endgültige (nicht rückgängig
// machbare) Aktionen - wird sowohl fürs Löschen von Gerichten als auch von
// Standorten benutzt. Rendert nichts, solange kein pendingItem übergeben wird.
// pendingItem hat die Form { kind: "Gericht" | "Standort", name: string }.
function ConfirmDialog({ pendingItem, onConfirm, onCancel }) {
  if (!pendingItem) {
    return null;
  }

  return (
    <div className={styles.confirmOverlay}>
      <div className={styles.confirmCard}>
        <h3>{pendingItem.kind} wirklich löschen?</h3>
        <p>
          "{pendingItem.name}" wird endgültig entfernt. Das kann nicht rückgängig
          gemacht werden.
        </p>
        <div className={styles.confirmActions}>
          <button className={styles.confirmCancelButton} type="button" onClick={onCancel}>
            Abbrechen
          </button>
          <button className={styles.confirmDeleteButton} type="button" onClick={onConfirm}>
            Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}

// Einzelner Eintrag in der Produktliste, inklusive Bild-Upload.
function ProductRow({ dish, locations, isUploading, onEdit, onToggleAvailability, onRequestDelete, onUploadImage }) {
  const fileInputRef = useRef(null);
  const locationNames = dish.locationIds
    .map((id) => locations.find((location) => location.id === id)?.name)
    .filter(Boolean);

  return (
    <article className={styles.libraryDish}>
      {dish.imageUrl ? (
        <img className={styles.dishThumb} src={dish.imageUrl} alt={dish.name} />
      ) : (
        <div className={styles.dishThumbPlaceholder}>
          <ImageIcon size={18} />
        </div>
      )}
      <div>
        <strong>{dish.name}</strong>
        {!dish.available && <span className={styles.inactiveBadge}>Ausgeblendet</span>}
        <p>{dish.description}</p>
        <span>{formatPrice(dish.priceCents, dish.currency)}</span>
        {locationNames.length > 0 && (
          <div className={styles.locationChips}>
            {locationNames.map((name) => (
              <span key={name} className={styles.locationChip}>
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.hiddenFileInput}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onUploadImage(dish.id, file);
          }
          event.target.value = "";
        }}
      />
      <button
        className={styles.neutralButton}
        type="button"
        disabled={isUploading}
        aria-label={`Bild für ${dish.name} hochladen`}
        onClick={() => fileInputRef.current?.click()}
      >
        {isUploading ? "…" : <ImageIcon size={17} />}
      </button>
      <button
        className={styles.primaryButton}
        type="button"
        aria-label={`${dish.name} bearbeiten`}
        onClick={() => onEdit(dish)}
      >
        <Pencil size={17} />
      </button>
      <button
        className={dish.available ? styles.dangerButton : styles.successButton}
        type="button"
        aria-label={dish.available ? `${dish.name} ausblenden` : `${dish.name} einblenden`}
        onClick={() => onToggleAvailability(dish)}
      >
        {dish.available ? <Ban size={17} /> : <CheckCircle2 size={17} />}
      </button>
      <button
        className={styles.dangerButton}
        type="button"
        aria-label={`${dish.name} endgültig löschen`}
        onClick={() => onRequestDelete(dish)}
      >
        <Trash2 size={17} />
      </button>
    </article>
  );
}

// Ein Tag im 14-Tage-Plan: Tagesgericht + zwei Sondergerichte aus dem Produktkatalog auswählen.
// isInherited zeigt an, dass diese Werte vom vorherigen Zyklus übernommen wurden (noch
// keine eigene Zuordnung für genau dieses Datum) - eine Auswahl hier legt eine eigene an.
// isClosed zeigt an, dass der Standort an diesem Wochentag laut seinen Einstellungen
// (serveSaturday/serveSunday) gar nicht bedient wird - dann gibt es nichts auszuwählen.
function DayPlanRow({ dateKey, isToday, products, slots, isInherited, isClosed, onChangeSlot }) {
  const date = new Date(`${dateKey}T12:00:00`);

  return (
    <article
      className={
        isToday
          ? `${styles.dayRow} ${styles.dayRowToday}`
          : isClosed
            ? `${styles.dayRow} ${styles.dayRowClosed}`
            : styles.dayRow
      }
    >
      <div className={styles.dayRowDate}>
        {isToday && <span className={styles.todayPill}>Heute</span>}
        <strong>{weekdayShortFormatter.format(date)}</strong>
        <span>{dateFormatter.format(date)}</span>
        {isInherited && !isClosed && (
          <span className={styles.cyclePill} title="Aus dem vorherigen 14-Tage-Zyklus übernommen">
            Zyklus
          </span>
        )}
      </div>
      {isClosed ? (
        <p className={styles.dayRowClosedNote}>
          Standort hat an diesem Tag laut Standort-Einstellungen geschlossen.
        </p>
      ) : (
      <div className={styles.dayRowSlots}>
        <label>
          Tagesgericht
          <select
            value={slots.fixedId || ""}
            onChange={(event) => onChangeSlot(dateKey, "fixedId", event.target.value)}
          >
            <option value="">- kein Gericht -</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sondergericht 1
          <select
            value={slots.special1Id || ""}
            onChange={(event) => onChangeSlot(dateKey, "special1Id", event.target.value)}
          >
            <option value="">- kein Gericht -</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sondergericht 2
          <select
            value={slots.special2Id || ""}
            onChange={(event) => onChangeSlot(dateKey, "special2Id", event.target.value)}
          >
            <option value="">- kein Gericht -</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      )}
    </article>
  );
}

function AdminSpeiseplan() {
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    isFirebaseConfigured
      ? Boolean(firebaseAuth?.currentUser)
      : window.sessionStorage.getItem("foodosia-admin") === "true"
  );
  const [loginError, setLoginError] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(LOCATION_ID);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [editingDish, setEditingDish] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  // Zeigt an, welches Gericht gerade ein Bild hochlädt, damit der Button
  // währenddessen sichtbar reagiert statt "nichts zu tun".
  const [uploadingId, setUploadingId] = useState(null);
  const [dailyPlan, setDailyPlan] = useState({});
  // Gericht oder Standort, für das gerade der "Wirklich löschen?"-Dialog offen ist
  // (siehe ConfirmDialog). Form: { kind: "Gericht" | "Standort", id, name }.
  const [pendingDelete, setPendingDelete] = useState(null);

  // Hält isLoggedIn synchron mit dem echten Firebase-Auth-Status (z.B. nach Token-Ablauf).
  useEffect(() => {
    if (!isFirebaseConfigured || !firebaseAuth) {
      return undefined;
    }

    return onIdTokenChanged(firebaseAuth, (user) => {
      setIsLoggedIn(Boolean(user));
    });
  }, []);

  // Rollendes Fenster: heute plus die nächsten 13 Tage. Wird täglich neu berechnet,
  // dadurch fallen vergangene Tage automatisch raus.
  const dateWindow = useMemo(() => buildDateWindow(14), []);
  const validityRange = `${rangeFormatter.format(
    new Date(`${dateWindow[0]}T12:00:00`)
  )} – ${rangeFormatter.format(new Date(`${dateWindow[dateWindow.length - 1]}T12:00:00`))}`;

  const reloadLocations = useCallback(() => {
    return loadAdminLocations()
      .then((locationData) => {
        setLocations(locationData);
        setSelectedLocation((currentLocation) =>
          locationData.some((location) => location.id === currentLocation)
            ? currentLocation
            : locationData[0]?.id || LOCATION_ID
        );
        return locationData;
      })
      .catch((error) => {
        setErrorMessage(error.message || "Standorte konnten nicht geladen werden.");
        return [];
      });
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      reloadLocations();
    }
  }, [isLoggedIn, reloadLocations]);

  const reloadProducts = useCallback((locationId = selectedLocation) => {
    setIsLoadingProducts(true);
    setErrorMessage("");

    return loadAdminProducts(locationId)
      .then((data) => {
        setProducts(data);
        setIsLoadingProducts(false);
      })
      .catch((error) => {
        setProducts([]);
        setErrorMessage(error.message || "Produkte konnten nicht geladen werden.");
        setIsLoadingProducts(false);
      });
  }, [selectedLocation]);

  useEffect(() => {
    if (isLoggedIn && selectedLocation) {
      reloadProducts(selectedLocation);
    }
  }, [isLoggedIn, selectedLocation, reloadProducts]);

  useEffect(() => {
    if (selectedLocation) {
      loadDailyPlan(selectedLocation)
        .then(setDailyPlan)
        .catch((error) => {
          setErrorMessage(error.message || "Tagesplan konnte nicht geladen werden.");
        });
    }
  }, [selectedLocation]);

  async function handleChangeDaySlot(dateKey, slotField, productId) {
    // Als Basis nehmen wir die evtl. vom Zyklus übernommenen Werte, damit beim Ändern
    // von nur einem Gericht nicht die beiden anderen (nur angezeigten) Gerichte verloren gehen.
    const { slots: currentSlots } = resolveCycleSlots(dailyPlan, dateKey);
    const nextSlots = { ...currentSlots, [slotField]: productId || undefined };

    try {
      setErrorMessage("");
      const updatedPlan = await saveDailyAssignment(selectedLocation, dateKey, nextSlots);
      setDailyPlan(updatedPlan);
    } catch (error) {
      setErrorMessage(error.message || "Tageszuordnung konnte nicht gespeichert werden.");
    }
  }

  const filteredDishes = products.filter((dish) =>
    `${dish.name} ${dish.description}`.toLowerCase().includes(query.toLowerCase())
  );

  // Aktive Standorte zuerst, damit sie bei vielen (auch inaktiven Test-)Einträgen
  // nicht in der Liste untergehen. Innerhalb der beiden Gruppen bleibt die
  // Backend-Reihenfolge erhalten.
  const sortedLocations = [...locations].sort(
    (a, b) => Number(b.active !== false) - Number(a.active !== false)
  );

  // Der komplette Standort-Datensatz des gerade bearbeiteten Standorts - u.a. für
  // die Wochenend-Einstellungen (serveSaturday/serveSunday) im 14-Tage-Plan unten.
  const currentLocation = locations.find((location) => location.id === selectedLocation);

  async function handleLogin(event) {
    event.preventDefault();
    setLoginError("");
    const formData = new FormData(event.currentTarget);

    if (!isFirebaseConfigured) {
      // Lokale Entwicklung ohne Firebase: das Backend akzeptiert dafür einen festen
      // Test-Token (siehe menuApi.js), echte Zugangsdaten sind hier nicht nötig.
      window.sessionStorage.setItem("foodosia-admin", "true");
      setIsLoggedIn(true);
      return;
    }

    try {
      await loginWithEmail({
        email: formData.get("email"),
        password: formData.get("password"),
      });
      // isLoggedIn wird automatisch über den onIdTokenChanged-Listener oben gesetzt.
    } catch (error) {
      setLoginError(error.message || "Anmeldung fehlgeschlagen.");
    }
  }

  async function handleLogout() {
    if (isFirebaseConfigured) {
      await logoutWithFirebase();
    } else {
      window.sessionStorage.removeItem("foodosia-admin");
      setIsLoggedIn(false);
    }
  }

  async function handleSubmitDish(dish, locationIds) {
    try {
      setErrorMessage("");

      if (dish.id) {
        await updateProduct(dish.id, dish, locationIds);
      } else {
        await createProduct(dish, locationIds);
      }

      setEditingDish(null);
      await reloadProducts(selectedLocation);
    } catch (error) {
      setErrorMessage(error.message || "Gericht konnte nicht gespeichert werden.");
    }
  }

  async function handleSubmitLocation(location) {
    try {
      setErrorMessage("");

      if (location.id) {
        await updateLocation(location.id, location);
      } else {
        await createLocation(location);
      }

      setEditingLocation(null);
      await reloadLocations();
    } catch (error) {
      setErrorMessage(error.message || "Standort konnte nicht gespeichert werden.");
    }
  }

  async function toggleLocationActive(location) {
    try {
      setErrorMessage("");
      await setLocationActive(location.id, !location.active);
      await reloadLocations();
    } catch (error) {
      setErrorMessage(error.message || "Standort konnte nicht geändert werden.");
    }
  }

  async function toggleProductAvailability(dish) {
    try {
      setErrorMessage("");
      await setProductAvailability(dish.id, !dish.available);
      await reloadProducts(selectedLocation);
    } catch (error) {
      setErrorMessage(error.message || "Gericht konnte nicht geändert werden.");
    }
  }

  async function handleUploadImage(productId, file) {
    try {
      setErrorMessage("");
      setUploadingId(productId);
      await uploadProductImage(productId, file);
      await reloadProducts(selectedLocation);
    } catch (error) {
      setErrorMessage(error.message || "Bild konnte nicht hochgeladen werden.");
    } finally {
      setUploadingId(null);
    }
  }

  // Öffnen die beiden folgenden Funktionen nur den Bestätigungs-Dialog - gelöscht
  // wird erst in handleConfirmDelete, nachdem der Nutzer aktiv bestätigt hat.
  function handleRequestDeleteProduct(dish) {
    setPendingDelete({ kind: "Gericht", id: dish.id, name: dish.name });
  }

  function handleRequestDeleteLocation(location) {
    setPendingDelete({ kind: "Standort", id: location.id, name: location.name });
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) {
      return;
    }

    try {
      setErrorMessage("");

      if (pendingDelete.kind === "Standort") {
        await deleteLocation(pendingDelete.id);
        await reloadLocations();
      } else {
        await deleteProduct(pendingDelete.id);
        await reloadProducts(selectedLocation);
      }
    } catch (error) {
      setErrorMessage(
        error.message ||
          `${pendingDelete.kind} konnte nicht gelöscht werden.`
      );
    } finally {
      setPendingDelete(null);
    }
  }

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} loginError={loginError} />;
  }

  return (
    <main className={styles.adminPage}>
      <header className={styles.adminHeader}>
        <div>
          <p className={styles.eyebrow}>Foodosia Admin</p>
          <h1>Speiseplan-Editor</h1>
          <p>Verwalte Standorte und Gerichte des Foodosia-Katalogs.</p>
          <label className={styles.adminLocationSelect}>
            <MapPin size={18} />
            <span>Standort bearbeiten</span>
            <select
              value={selectedLocation}
              onChange={(event) => setSelectedLocation(event.target.value)}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                  {!location.active ? " (inaktiv)" : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className={styles.headerActions}>
          <Link to="/speiseplan">Speiseplan ansehen</Link>
          <button type="button" onClick={handleLogout}>
            <LogOut size={17} />
            Abmelden
          </button>
        </div>
      </header>

      {errorMessage && <div className={styles.errorBox}>{errorMessage}</div>}

      <section className={styles.locationPanel}>
        <div className={styles.panelTitle}>
          <div>
            <h2>Standorte verwalten</h2>
            <p>Standorte anlegen, bearbeiten und ein- oder ausblenden.</p>
          </div>
          <span>{locations.length}</span>
        </div>
        <LocationForm
          editingLocation={editingLocation}
          onCancelEdit={() => setEditingLocation(null)}
          onSubmitLocation={handleSubmitLocation}
        />
        <div className={styles.locationList}>
          {sortedLocations.map((location) => (
            <article
              className={
                location.active === false
                  ? `${styles.locationItem} ${styles.locationItemInactive}`
                  : styles.locationItem
              }
              key={location.id}
            >
              <button
                className={styles.locationMainButton}
                type="button"
                onClick={() => setSelectedLocation(location.id)}
              >
                <strong>
                  <span className={styles.locationNameText}>{location.name}</span>
                  {!location.active && <span className={styles.inactiveBadge}>Inaktiv</span>}
                </strong>
                <span>{location.address || location.id}</span>
              </button>
              <button
                className={styles.primaryButton}
                type="button"
                aria-label={`${location.name} bearbeiten`}
                onClick={() => setEditingLocation(location)}
              >
                <Pencil size={17} />
              </button>
              <button
                className={location.active ? styles.dangerButton : styles.successButton}
                type="button"
                aria-label={location.active ? `${location.name} deaktivieren` : `${location.name} aktivieren`}
                onClick={() => toggleLocationActive(location)}
              >
                {location.active ? <Ban size={17} /> : <CheckCircle2 size={17} />}
              </button>
              <button
                className={styles.dangerButton}
                type="button"
                aria-label={`${location.name} endgültig löschen`}
                onClick={() => handleRequestDeleteLocation(location)}
              >
                <Trash2 size={17} />
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.editorGrid}>
        <aside className={styles.library}>
          <div className={styles.panelTitle}>
            <h2>Gericht anlegen</h2>
          </div>
          <DishForm
            editingDish={editingDish}
            locations={locations}
            defaultLocationId={selectedLocation}
            onCancelEdit={() => setEditingDish(null)}
            onSubmitDish={handleSubmitDish}
          />
        </aside>

        <section className={styles.planPanel}>
          <div className={styles.panelTitle}>
            <div>
              <h2>Produkte</h2>
              <p>Gerichte für den ausgewählten Standort.</p>
            </div>
            <span>{products.length}</span>
          </div>
          <div className={styles.searchBox}>
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Gerichte suchen..."
            />
          </div>

          {isLoadingProducts ? (
            <div className={styles.loading}>Produkte werden geladen...</div>
          ) : filteredDishes.length === 0 ? (
            <p className={styles.emptyText}>Für diesen Standort sind noch keine Gerichte angelegt.</p>
          ) : (
            <div className={styles.dishLibraryList}>
              {filteredDishes.map((dish) => (
                <ProductRow
                  key={dish.id}
                  dish={dish}
                  locations={locations}
                  isUploading={uploadingId === dish.id}
                  onEdit={setEditingDish}
                  onToggleAvailability={toggleProductAvailability}
                  onRequestDelete={handleRequestDeleteProduct}
                  onUploadImage={handleUploadImage}
                />
              ))}
            </div>
          )}
        </section>
      </section>

      <section className={styles.dayPlanPanel}>
        <div className={styles.panelTitle}>
          <div>
            <h2>
              <CalendarDays size={20} />
              14-Tage-Speiseplan
            </h2>
            <p>
              Tagesgericht und zwei Sondergerichte pro Tag für diesen Standort. Der Plan
              wiederholt sich danach automatisch alle 14 Tage - Tage mit dem "Zyklus"-Badge
              zeigen die übernommenen Werte, eine Auswahl legt eine eigene Zuordnung nur für
              diesen einen Tag an.
            </p>
            <p className={styles.validityRange}>
              Gültig: {validityRange} · Änderungen werden beim Auswählen sofort gespeichert.
            </p>
          </div>
        </div>
        <div className={styles.dayRowList}>
          {dateWindow.map((dateKey, index) => {
            const { slots, isInherited } = resolveCycleSlots(dailyPlan, dateKey);
            // getDay(): 0 = Sonntag, 6 = Samstag. Standardmäßig (kein Standort
            // gefunden oder Feld nicht gesetzt) gilt das Wochenende als geschlossen.
            const weekday = new Date(`${dateKey}T12:00:00`).getDay();
            const isClosed =
              (weekday === 6 && !currentLocation?.serveSaturday) ||
              (weekday === 0 && !currentLocation?.serveSunday);

            return (
              <DayPlanRow
                key={dateKey}
                dateKey={dateKey}
                isToday={index === 0}
                products={products}
                slots={slots}
                isInherited={isInherited}
                isClosed={isClosed}
                onChangeSlot={handleChangeDaySlot}
              />
            );
          })}
        </div>
      </section>

      <ConfirmDialog
        pendingItem={pendingDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </main>
  );
}

export default AdminSpeiseplan;
